import { useState } from 'react';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { RadioButton } from 'primereact/radiobutton';
import { useTranslation } from 'react-i18next';

import {
  parseExistingComments,
  parseExistingCommentsV2,
} from '@/components/common/revisions-button/utils';

import {
  CommentDialogProps,
  CommentItem,
  CommentType,
} from './types/comment-dialog';

export const CommentDialog = <T,>({
  config,
  setValue,
  getValue,
  onClose,
  initialSelectedCommentType,
  allowDelete = true,
  useImprovedParsing = false,
}: CommentDialogProps<T>) => {
  const { t } = useTranslation();
  const initialCommentType = initialSelectedCommentType
    ? config.commentTypes.find((ct) => ct.id === initialSelectedCommentType) ||
      null
    : null;

  const [selectedCommentType, setSelectedCommentType] =
    useState<CommentType | null>(initialCommentType);
  const [additionalInputValue, setAdditionalInputValue] = useState('');
  const [secondaryInputValue, setSecondaryInputValue] = useState('');
  const [radioSelection, setRadioSelection] = useState<Record<string, string>>(
    {},
  );
  const [commentList, setCommentList] = useState<CommentItem[]>(() => {
    const existingComments = getValue(config.existingCommentsFieldName);
    if (existingComments) {
      const parser = useImprovedParsing
        ? parseExistingCommentsV2
        : parseExistingComments;
      return parser(existingComments).map((comment) => ({ comment }));
    }
    return [];
  });
  const [showSecondaryInput, setShowSecondaryInput] = useState(false);

  const handleCommentTypeChange = (commentTypeId: string) => {
    const commentType = config.commentTypes.find(
      (ct) => ct.id === commentTypeId,
    );
    setSelectedCommentType(commentType || null);

    if (commentTypeId === 'did-not-aspirate') {
      setAdditionalInputValue(t('aspiration.didNotAspirate'));
    } else if (commentTypeId === 'do-not-fert') {
      setAdditionalInputValue(t('fertilization.doNotFert'));
    } else {
      setAdditionalInputValue('');
    }

    setRadioSelection({});
    setSecondaryInputValue('');
    setShowSecondaryInput(false);
  };

  const handleAddComment = () => {
    if (!selectedCommentType) return;

    let comment = selectedCommentType.label;

    if (selectedCommentType.requiresAdditionalInput) {
      if (selectedCommentType.additionalInputType === 'textarea') {
        if (selectedCommentType.customLogic) {
          comment = selectedCommentType.customLogic(comment, {
            radioSelection,
            additionalInputValue,
            secondaryInputValue,
          });
        } else {
          comment = additionalInputValue;
        }
      } else if (selectedCommentType.additionalInputType === 'number') {
        if (additionalInputValue) {
          if (selectedCommentType.customLogic) {
            comment = selectedCommentType.customLogic(comment, {
              radioSelection,
              additionalInputValue,
              secondaryInputValue,
            });
          } else {
            comment += `: ${additionalInputValue} ${t('common.days')}`;
          }
        }
      } else if (selectedCommentType.additionalInputType === 'radio') {
        const selectedValue = radioSelection[selectedCommentType.id];
        if (selectedValue) {
          if (selectedCommentType.customLogic) {
            comment = selectedCommentType.customLogic(comment, {
              radioSelection,
              additionalInputValue,
              secondaryInputValue,
            });
          } else {
            comment += `, ${selectedValue}`;
          }
        }
      }
    }

    if (
      selectedCommentType.customLogic &&
      !selectedCommentType.requiresAdditionalInput
    ) {
      comment = selectedCommentType.customLogic(comment, {
        radioSelection,
        additionalInputValue,
        secondaryInputValue,
      });
    }

    if (comment) {
      setCommentList([...commentList, { comment }]);
      setSelectedCommentType(null);
      setAdditionalInputValue('');
      setSecondaryInputValue('');
      setRadioSelection({});
      setShowSecondaryInput(false);
    }
  };

  const handleRemoveComment = (commentObject: CommentItem) => {
    setCommentList(
      commentList.filter((c) => c.comment !== commentObject.comment),
    );
  };

  const handleSaveComments = () => {
    const combinedComments = commentList.map((c) => c.comment).join('. ');

    setValue(config.existingCommentsFieldName, combinedComments);
    onClose();
  };

  const renderAdditionalInput = () => {
    if (!selectedCommentType?.requiresAdditionalInput) return null;

    const {
      additionalInputType,
      additionalInputLabel,
      additionalInputOptions,
      additionalInputPlaceholder,
      additionalInputMin,
      additionalInputMax,
      additionalInputRows,
      additionalInputCols,
      secondaryInputType,
      conditionalOptions,
    } = selectedCommentType;

    const isAdhesionComment = selectedCommentType.id.startsWith('adhesion-');
    const hasRateSelection =
      isAdhesionComment && radioSelection[selectedCommentType.id];
    const shouldShowInstructions = isAdhesionComment && hasRateSelection;

    if (shouldShowInstructions && !showSecondaryInput) {
      setShowSecondaryInput(true);
    }

    switch (additionalInputType) {
      case 'textarea':
        return (
          <InputTextarea
            value={additionalInputValue}
            onChange={(e) => setAdditionalInputValue(e.target.value)}
            rows={additionalInputRows || 3}
            cols={additionalInputCols || 30}
            placeholder={additionalInputPlaceholder}
          />
        );

      case 'number':
        return (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <label
                htmlFor={`${selectedCommentType.id}-input`}
                className="whitespace-nowrap"
              >
                {additionalInputLabel}:
              </label>
              <InputText
                id={`${selectedCommentType.id}-input`}
                value={additionalInputValue}
                onChange={(e) => setAdditionalInputValue(e.target.value)}
              />
            </div>
            {secondaryInputType && (
              <div className="flex flex-col gap-2">
                <label htmlFor={`${selectedCommentType.id}-secondary`}>
                  {selectedCommentType.secondaryInputLabel}:
                </label>
                {secondaryInputType === 'textarea' && (
                  <InputTextarea
                    id={`${selectedCommentType.id}-secondary`}
                    value={secondaryInputValue}
                    onChange={(e) => setSecondaryInputValue(e.target.value)}
                    rows={selectedCommentType.secondaryInputRows || 1}
                    cols={selectedCommentType.secondaryInputCols || 30}
                    placeholder={selectedCommentType.secondaryInputPlaceholder}
                  />
                )}
                {secondaryInputType === 'number' && (
                  <InputText
                    id={`${selectedCommentType.id}-secondary`}
                    type="number"
                    value={secondaryInputValue}
                    onChange={(e) => setSecondaryInputValue(e.target.value)}
                    placeholder={selectedCommentType.secondaryInputPlaceholder}
                  />
                )}
              </div>
            )}
          </div>
        );

      case 'radio': {
        const options =
          conditionalOptions?.[selectedCommentType.id] ||
          additionalInputOptions ||
          [];
        return (
          <div className="flex flex-col gap-2">
            <div>
              <strong>{additionalInputLabel}:</strong>
            </div>
            {options.map((option) => (
              <div key={option.value} className="flex items-center">
                <RadioButton
                  inputId={`${selectedCommentType.id}-${option.value}`}
                  name={selectedCommentType.id}
                  value={option.value}
                  onChange={(e) => {
                    const newSelection = {
                      ...radioSelection,
                      [selectedCommentType.id]: e.value,
                    };
                    setRadioSelection(newSelection);
                    if (isAdhesionComment) {
                      setShowSecondaryInput(true);
                    }
                  }}
                  checked={
                    radioSelection[selectedCommentType.id] === option.value
                  }
                  className="mr-2"
                />
                <label htmlFor={`${selectedCommentType.id}-${option.value}`}>
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );
      }

      case 'dropdown':
        return (
          <Dropdown
            value={additionalInputValue}
            options={additionalInputOptions}
            onChange={(e) => setAdditionalInputValue(e.value)}
            placeholder={additionalInputPlaceholder}
          />
        );

      default:
    }
  };

  const renderSecondaryInput = () => {
    if (!selectedCommentType || !showSecondaryInput) return null;

    const isAdhesionComment = selectedCommentType.id.startsWith('adhesion-');
    if (!isAdhesionComment) return null;

    const instructionOptions =
      selectedCommentType.id === 'too-far-pregnant'
        ? [{ label: t('common.done'), value: 'DONE' }]
        : selectedCommentType.id.startsWith('adhesion')
          ? [
              { label: t('rest.noRest'), value: 'no rest' },
              { label: t('rest.threeWeekRest'), value: '3 week rest' },
              { label: t('rest.fourWeekRest'), value: '4 week rest' },
            ]
          : [
              { label: t('rest.noRest'), value: 'no rest' },
              { label: t('rest.threeWeekRest'), value: '3 week rest' },
              { label: t('rest.fourWeekRest'), value: '4 week rest' },
              { label: t('common.done'), value: 'DONE' },
            ];

    return (
      <div className="flex flex-col gap-2">
        <div>
          <strong>{t('common.instructions')}:</strong>
        </div>
        {instructionOptions.map((option) => (
          <div key={option.value} className="flex items-center">
            <RadioButton
              inputId={`${selectedCommentType.id}-instruction-${option.value}`}
              name={`${selectedCommentType.id}-instruction`}
              value={option.value}
              onChange={(e) =>
                setRadioSelection({
                  ...radioSelection,
                  [`${selectedCommentType.id}-instruction`]: e.value,
                })
              }
              checked={
                radioSelection[`${selectedCommentType.id}-instruction`] ===
                option.value
              }
              className="mr-2"
            />
            <label
              htmlFor={`${selectedCommentType.id}-instruction-${option.value}`}
            >
              {option.label}
            </label>
          </div>
        ))}
      </div>
    );
  };

  const commentTypeOptions = config.commentTypes.map((ct) => ({
    label: ct.label,
    value: ct.id,
  }));

  return (
    <div className="flex flex-col gap-5">
      {config.title && (
        <h3 className="m-0 mb-3 text-gray-900">{config.title}</h3>
      )}

      <div className="flex flex-row flex-wrap gap-5">
        <Dropdown
          value={selectedCommentType?.id || ''}
          options={commentTypeOptions}
          onChange={(e) => handleCommentTypeChange(e.value)}
          placeholder={config.placeholder || t('selectComment.selectAComment')}
          className="h-12 w-80"
        />

        {selectedCommentType && renderAdditionalInput()}
        {renderSecondaryInput()}
      </div>

      <Button
        className="w-45"
        label={config.addButtonLabel || t('common.add')}
        onClick={handleAddComment}
        disabled={!selectedCommentType}
      />

      <DataTable value={commentList} scrollable scrollHeight="400px">
        <Column
          field="comment"
          header={config.commentTableHeader || t('common.comment')}
        />
        {config.includeDeleteButton && allowDelete && (
          <Column
            body={(rowData) => (
              <Button
                icon={config.deleteButtonIcon || 'pi pi-trash'}
                onClick={() => handleRemoveComment(rowData)}
                className="p-button-danger p-button-text"
              />
            )}
          />
        )}
      </DataTable>

      <div className="flex justify-end gap-2">
        <Button
          label={config.closeButtonLabel || t('common.close')}
          icon="pi pi-times"
          onClick={onClose}
        />
        <Button
          label={config.saveButtonLabel || t('common.save')}
          onClick={handleSaveComments}
        />
      </div>
    </div>
  );
};
